/**
 * Client-side event date/time/price formatting.
 */

function parseTimeParts(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return { hours: parseInt(match[1], 10), minutes: parseInt(match[2], 10) };
}

export function formatTime12(timeStr) {
  const parts = parseTimeParts(timeStr);
  if (!parts) return null;
  const { hours, minutes } = parts;
  const period = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${hour12}.${String(minutes).padStart(2, '0')} ${period}`;
}

export function formatCardDateLong(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-KE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

export function formatDisplayDate(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-KE', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function isSameCalendarDay(a, b) {
  if (!a || !b) return false;
  const d1 = new Date(a);
  const d2 = new Date(b);
  return d1.getFullYear() === d2.getFullYear()
    && d1.getMonth() === d2.getMonth()
    && d1.getDate() === d2.getDate();
}

export function formatDateRange(startDate, endDate) {
  const from = formatDisplayDate(startDate);
  if (!from) return null;
  if (!endDate) return from;
  const to = formatDisplayDate(endDate);
  if (!to || isSameCalendarDay(startDate, endDate)) return from;
  return `${from} - ${to}`;
}

export function formatTimeRange(startTime, endTime) {
  const start = formatTime12(startTime);
  const end = formatTime12(endTime);
  if (start && end) return `${start} - ${end}`;
  if (start && !end) return `${start} till late`;
  return end || null;
}

/** Paid packages only — complimentary / free organiser passes are never shown publicly. */
export function getPublicPricingTiers(tiers) {
  return (tiers || []).filter((t) => {
    if (!t || t.isActive === false) return false;
    if (!(Number(t.price) > 0)) return false;
    if (/complimentary|comp\b|guest pass/i.test(String(t.name || ''))) return false;
    return true;
  });
}

export function sortPricingTiersByPrice(tiers, featuredPrice = null) {
  const featured = featuredPrice != null ? Number(featuredPrice) : null;
  return [...(tiers || [])].sort((a, b) => {
    if (featured != null && !Number.isNaN(featured)) {
      const aMatch = Number(a.price) === featured;
      const bMatch = Number(b.price) === featured;
      if (aMatch && !bMatch) return -1;
      if (bMatch && !aMatch) return 1;
    }
    return Number(a.price) - Number(b.price);
  });
}

export function getStartingPrice(event) {
  if (event.startingPrice != null) return Number(event.startingPrice);

  const tiers = getPublicPricingTiers(event.pricingTiers);
  const tierPrices = tiers.map((t) => Number(t.price)).filter((p) => !Number.isNaN(p));
  const featured = Number(event.price);

  // Prefer event.price when it matches a package (featured / primary ticket amount)
  if (!Number.isNaN(featured) && tierPrices.includes(featured)) {
    return featured;
  }

  if (tierPrices.length) return Math.min(...tierPrices);

  const prices = [featured];
  if (event.earlyBirdPrice != null) prices.push(Number(event.earlyBirdPrice));
  return Math.min(...prices.filter((p) => !Number.isNaN(p)));
}

export function getStartingOriginalPrice(event) {
  if (event.startingOriginalPrice != null) {
    const n = Number(event.startingOriginalPrice);
    return !Number.isNaN(n) && n > getStartingPrice(event) ? n : null;
  }

  const starting = getStartingPrice(event);
  const tiers = getPublicPricingTiers(event.pricingTiers);
  const match = tiers.find((t) => Number(t.price) === starting);
  const original = match?.originalPrice != null ? Number(match.originalPrice) : null;
  if (original != null && !Number.isNaN(original) && original > starting) return original;
  return null;
}

export function formatTierPriceDisplay(tier) {
  const price = Number(tier?.price);
  const original = tier?.originalPrice != null ? Number(tier.originalPrice) : null;
  const hasDiscount = original != null && !Number.isNaN(original) && original > price;
  return {
    price,
    original: hasDiscount ? original : null,
    hasDiscount,
    priceLabel: price === 0 ? 'Free' : formatKesPrice(price),
    originalLabel: hasDiscount ? formatKesPrice(original) : null
  };
}

export function formatKesPrice(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return 'KES 0';
  if (value === 0) return 'Free';
  return `KES ${value.toLocaleString('en-KE')}`;
}

export function formatStartingFrom(event) {
  const price = getStartingPrice(event);
  if (price === 0) return 'Free';
  return `${price.toLocaleString('en-KE')}/=`;
}

export function hasMultiplePackages(event) {
  const paid = getPublicPricingTiers(event.pricingTiers);
  return paid.length > 0 || event.hasPackages === true;
}

export function isMultiDayEvent(startDate, endDate) {
  if (!startDate || !endDate) return false;
  return !isSameCalendarDay(startDate, endDate);
}

export function formatMultiDayRangeLabel(startDate, endDate) {
  if (!isMultiDayEvent(startDate, endDate)) return null;
  const from = formatDisplayDate(startDate);
  const to = formatDisplayDate(endDate);
  if (!from || !to) return null;
  return { from, to };
}

function getEndOfCalendarDay(dateValue) {
  if (!dateValue) return null;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function getLastEventDay(event) {
  if (!event?.date) return null;
  return event.endDate || event.date;
}

export function isEventSalesClosed(event, now = new Date()) {
  if (!event) return false;
  if (event.salesClosed === true) return true;
  if (event.status === 'CANCELLED' || event.status === 'SOLD_OUT' || event.status === 'COMPLETED') {
    return true;
  }
  const endOfSales = getEndOfCalendarDay(getLastEventDay(event));
  if (!endOfSales) return false;
  return now > endOfSales;
}

export function isEventPast(event, now = new Date()) {
  if (!event) return false;
  if (event.isPast === true) return true;
  if (event.status === 'COMPLETED') return true;
  const endOfSales = getEndOfCalendarDay(getLastEventDay(event));
  if (!endOfSales) return false;
  return now > endOfSales;
}

export function getEventSalesClosedReason(event) {
  if (event?.salesClosedReason) return event.salesClosedReason;
  if (event?.status === 'SOLD_OUT') return 'sold_out';
  if (event?.status === 'CANCELLED') return 'cancelled';
  if (isEventPast(event)) return 'ended';
  return null;
}

export function getEventDateBadgeParts(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return {
    weekday: date.toLocaleDateString('en-KE', { weekday: 'short' }).toUpperCase(),
    day: date.getDate(),
    month: date.toLocaleDateString('en-KE', { month: 'short' }).toUpperCase(),
    year: date.getFullYear()
  };
}

export function getEventDisplay(event) {
  if (event.display) return event.display;
  return {
    dateFrom: formatDisplayDate(event.date),
    dateTo: formatDisplayDate(event.endDate || event.date),
    dateRange: formatDateRange(event.date, event.endDate),
    timeRange: formatTimeRange(event.startTime, event.endTime)
  };
}

export function isVirtualVenue(venue) {
  if (!venue) return false;
  const city = venue.city?.trim().toLowerCase();
  const name = venue.name?.trim().toLowerCase() || '';
  if (city === 'online') return true;
  if (name.startsWith('online')) return true;
  const address = venue.address?.trim() || '';
  return /^https?:\/\//i.test(address);
}

export function isVenueTba(venue) {
  if (!venue) return false;
  const city = venue.city?.trim().toUpperCase();
  const name = venue.name?.trim().toLowerCase() || '';
  return city === 'TBA' || name.includes('to be announced');
}

export function getVirtualEventUrl(venue) {
  if (!venue || !isVirtualVenue(venue)) return null;
  const address = venue.address?.trim();
  return address && /^https?:\/\//i.test(address) ? address : null;
}

/** Short label for cards and list rows - avoids long meeting URLs on virtual events. */
export function getEventLocationShort(event) {
  const venue = event?.venue;
  if (!venue) return null;
  if (isVirtualVenue(venue)) return 'Virtual';
  if (isVenueTba(venue)) return 'Venue to be announced';
  return [venue.name, venue.city].filter(Boolean).join(', ') || null;
}

/** Tighter location for hero featured card - drops parenthetical venue names. */
export function getFeaturedHeroLocation(event) {
  const venue = event?.venue;
  if (!venue) return '';
  if (isVirtualVenue(venue)) return 'Virtual';
  if (isVenueTba(venue)) return 'Venue TBA';
  let name = venue.name?.trim() || '';
  const parenIdx = name.indexOf('(');
  if (parenIdx > 0) name = name.slice(0, parenIdx).trim();
  const city = venue.city?.trim();
  return [name, city].filter(Boolean).join(', ');
}

/** Two-line teaser for hero featured card. */
export function getFeaturedHeroBrief(event) {
  let text = event?.description?.trim();
  if (!text && event?.shortDesc) {
    text = event.shortDesc
      .split('\n\n')
      .map((block) => block.trim())
      .filter((block) => block && !block.startsWith('Host:'))
      .join(' ')
      .trim();
  }
  if (!text) {
    const category = event?.category?.name;
    return category
      ? `Join us for a ${category.toLowerCase()} experience you won't want to miss.`
      : 'An unforgettable experience awaits - secure your spot today.';
  }
  return text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Title-case ticket tier descriptions; short all-caps tokens (e.g. VIP) stay as entered. */
export function formatTierDescription(text) {
  if (!text?.trim()) return '';

  const stopWords = new Set(['OF', 'THE', 'A', 'AN', 'AND', 'OR', 'FOR', 'TO', 'IN', 'ON', 'AT', 'BY']);

  return text
    .trim()
    .split(/\s+/)
    .map((word) => {
      const match = word.match(/^([^\w]*)([\w\d]+)([^\w]*)$/);
      if (!match) return word;

      const [, prefix, core, suffix] = match;

      if (/^\d+$/.test(core)) return word;
      if (/^[A-Z]{2,4}$/.test(core) && !stopWords.has(core)) {
        return `${prefix}${core}${suffix}`;
      }

      const lower = core.toLowerCase();
      return `${prefix}${lower.charAt(0).toUpperCase()}${lower.slice(1)}${suffix}`;
    })
    .join(' ');
}

/** Detail page: label, optional subline, and join URL for virtual events. */
export function getEventLocationDetail(event) {
  const venue = event?.venue;
  if (!venue) return { label: null, sublabel: null, url: null };
  if (isVirtualVenue(venue)) {
    return { label: 'Virtual', sublabel: null, url: getVirtualEventUrl(venue) };
  }
  if (isVenueTba(venue)) {
    return { label: 'Venue to be announced', sublabel: null, url: null };
  }
  return {
    label: venue.name,
    sublabel: [venue.address, venue.city].filter(Boolean).join(', ') || null,
    url: null
  };
}

export function parseEventHostName(shortDesc) {
  if (!shortDesc || typeof shortDesc !== 'string') return null;
  const firstLine = shortDesc.split(/\r?\n/)[0]?.trim();
  if (!firstLine?.startsWith('Host:')) return null;
  return firstLine.replace(/^Host:\s*/, '').trim() || null;
}

function looksLikeRefundPolicy(text) {
  return /\b(refund|ticket sales are final|rescheduled by the organizer|original payment method)\b/i.test(
    String(text || '')
  );
}

function stripInventoryNoise(text) {
  return String(text || '')
    .replace(/\b\d[\d,]*\s*(tickets?|spots?|seats?)\s*(available|left|remaining)\b/gi, ' ')
    .replace(/\b(only\s+)?\d[\d,]*\s*(left|remaining)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripHtmlLite(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractActivitiesLine(description) {
  const text = stripHtmlLite(description);
  if (!text) return null;
  const patterns = [
    /activities?\s*(?:include|including|:)\s*([^.!?\n]{8,180})/i,
    /including\s+([^.!?\n]{8,180}(?:castles?|slides?|painting|games?|music|food|workshops?)[^.!?\n]{0,80})/i,
    /(?:featuring|with)\s+([^.!?\n]{8,160}(?:,\s*|\s+and\s+)[^.!?\n]{8,80})/i
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m?.[1]) {
      const line = stripInventoryNoise(m[1]).replace(/[,;]\s*$/, '').trim();
      if (line.length >= 8 && !looksLikeRefundPolicy(line)) {
        return line.length > 140 ? `${line.slice(0, 139).trim()}…` : line;
      }
    }
  }
  return null;
}

/**
 * WhatsApp / OG highlight: tags or activities — never refund policy or ticket counts.
 */
export function buildEventShareHighlight(event) {
  const tags = (event?.tags || [])
    .map((t) => String(t || '').trim())
    .filter(Boolean)
    .slice(0, 6);
  if (tags.length) return tags.join(' · ');

  const categoryName = event?.category?.name || event?.categories?.[0]?.name;
  const activities = extractActivitiesLine(event?.description);
  if (activities) {
    return categoryName ? `${categoryName} · ${activities}` : activities;
  }

  const desc = stripInventoryNoise(stripHtmlLite(event?.description || ''));
  if (desc && !looksLikeRefundPolicy(desc)) {
    const first = desc.split(/(?<=[.!?])\s+/)[0] || desc;
    return first.length > 140 ? `${first.slice(0, 139).trim()}…` : first;
  }

  if (categoryName) return categoryName;
  return null;
}

/** Public-facing host label (custom host name when admin creates on behalf of someone).
 *  Never expose host/organizer email on public pages.
 */
export function getEventHostDisplay(event) {
  const name =
    event?.hostDisplayName ||
    parseEventHostName(event?.shortDesc) ||
    event?.organizer?.name ||
    null;

  return {
    name,
    email: null
  };
}

/**
 * Short code for shareable event links.
 * We use the last 10 characters of the event CUID (e.g. "...5r3y8ox7em").
 */
export function getEventShortCode(eventId, codeLength = 10) {
  if (!eventId) return null;
  const id = String(eventId);
  if (!id.length) return null;
  return id.slice(-codeLength);
}

export function getEventShortPath(eventId, codeLength = 10) {
  const code = getEventShortCode(eventId, codeLength);
  return code ? `/e/${code}` : '/e/unknown';
}

export function getEventShortAbsoluteUrl(eventId, codeLength = 10) {
  const path = getEventShortPath(eventId, codeLength);
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
}
